import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSlider } from '../../contexts/SliderContext';
import { getImageUrl } from '../../utils/imageUtils';

export function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [activeSlides, setActiveSlides] = useState([]);
  const containerRef = useRef(null);
  const { sliderItems, loading } = useSlider();

  const x = useMotionValue(0);

  // Filter and sort active slides
  useEffect(() => {
    const active = sliderItems
      .filter(slide => slide.isActive)
      .sort((a, b) => a.order - b.order);
    setActiveSlides(active);
  }, [sliderItems]);

  // Auto-slide effect
  useEffect(() => {
    if (activeSlides.length <= 1) return undefined;

    const timer = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % activeSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [activeSlides.length]);

  // Animate carousel on index change
  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth || 1;
      const targetX = -index * containerWidth;

      animate(x, targetX, {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      });
    }
  }, [index, x]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-dark to-primary-mid px-4">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 border-b-2 border-white mx-auto mb-3 sm:mb-4"></div>
          <p className="text-base sm:text-lg md:text-xl font-montserrat">Loading...</p>
        </div>
      </div>
    );
  }

  // No slides available
  if (activeSlides.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-dark to-primary-mid px-4">
        <p className="text-white text-base sm:text-lg md:text-xl">No slides available</p>
      </div>
    );
  }

  return (
    <div className='w-full h-screen min-h-[500px] sm:min-h-[600px] overflow-hidden'>
      <div className='relative overflow-hidden h-full md:pt-20 w-full' ref={containerRef}>
        <motion.div className='flex h-full w-full' style={{ x }}>
          {activeSlides.map((slide, idx) => {
            const imageUrl = getImageUrl(slide.image);
            const overlayOpacity = slide.overlayOpacity || 20;

            return (
              <div key={slide._id} className='shrink-0 w-full h-full relative overflow-hidden'>
                {/* Background Image */}
                <img
                  src={imageUrl}
                  alt={slide.caption || 'Campus slide'}
                  className='w-full h-full object-cover select-none pointer-events-none'
                  draggable={false}
                  fetchpriority={idx === 0 ? "high" : "auto"}
                  loading={idx === 0 ? "eager" : "lazy"}
                />
                
                {/* Overlay */}
                <div 
                  className="absolute inset-0 bg-black"
                  style={{ opacity: overlayOpacity / 100 }}
                />

                {/* Content Overlay */}
                {slide.caption && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 px-4 sm:px-6 md:px-8">
                    <div className="text-center px-2 xs:px-4 sm:px-6 max-w-xs xs:max-w-sm sm:max-w-xl md:max-w-2xl lg:max-w-4xl">
                      <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-3 xs:mb-4 sm:mb-5 md:mb-6 drop-shadow-2xl leading-tight"
                      >
                        {slide.caption}
                      </motion.h1>
                      {slide.buttonText && slide.buttonLink && (
                        <motion.a
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5, duration: 0.5 }}
                          href={slide.buttonLink}
                          className="btn-primary inline-block px-5 xs:px-6 sm:px-7 md:px-8 py-2.5 xs:py-3 sm:py-3.5 md:py-4 rounded-full hover:scale-105 text-sm xs:text-base sm:text-lg shadow-2xl touch-target"
                        >
                          {slide.buttonText}
                        </motion.a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </motion.div>

        {/* Previous Button */}
        {activeSlides.length > 1 && (
          <motion.button
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            className={`absolute left-2 xs:left-3 sm:left-4 md:left-6 lg:left-8 top-1/2 -translate-y-1/2 
              w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16
              rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 z-20 touch-target
              ${
                index === 0
                  ? 'opacity-40 cursor-not-allowed bg-white/30'
                  : 'bg-white/30 backdrop-blur-md border-2 border-white/50 hover:bg-white/50 hover:scale-110 active:scale-95'
              }`}
            whileHover={{ scale: index === 0 ? 1 : 1.1 }}
            whileTap={{ scale: index === 0 ? 1 : 0.95 }}
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5 xs:w-6 xs:h-6 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white drop-shadow-lg" strokeWidth={2.5} />
          </motion.button>
        )}

        {/* Next Button */}
        {activeSlides.length > 1 && (
          <motion.button
            disabled={index === activeSlides.length - 1}
            onClick={() => setIndex((i) => Math.min(activeSlides.length - 1, i + 1))}
            className={`absolute right-2 xs:right-3 sm:right-4 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 
              w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16
              rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 z-20 touch-target
              ${
                index === activeSlides.length - 1
                  ? 'opacity-40 cursor-not-allowed bg-white/30'
                  : 'bg-white/30 backdrop-blur-md border-2 border-white/50 hover:bg-white/50 hover:scale-110 active:scale-95'
              }`}
            whileHover={{ scale: index === activeSlides.length - 1 ? 1 : 1.1 }}
            whileTap={{ scale: index === activeSlides.length - 1 ? 1 : 0.95 }}
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5 xs:w-6 xs:h-6 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white drop-shadow-lg" strokeWidth={2.5} />
          </motion.button>
        )}

        {/* Progress Indicators - Desktop Only */}
        {activeSlides.length > 1 && (
          <div className='hidden md:flex absolute bottom-4 xs:bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 gap-1 xs:gap-1.5 sm:gap-2 md:gap-2 p-2 xs:p-2.5 sm:p-3 md:p-2 bg-white/20 backdrop-blur-sm rounded-lg xs:rounded-xl border border-white/30 z-20'>
            {activeSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-0.5 xs:h-1 sm:h-1.5 md:h-1.5 rounded-full transition-all touch-target ${
                  i === index 
                    ? 'w-3 xs:w-4 sm:w-5 md:w-6 bg-white shadow-lg' 
                    : 'w-3 xs:w-4 sm:w-5 md:w-2 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
